import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { useAuth } from './TestAuthContext';
import api from '../utils/api';

// Permission constants matching backend
export const Permissions = {
  // Claims
  CLAIMS_CREATE: 'claims:create',
  CLAIMS_EDIT_OWN: 'claims:edit_own',
  CLAIMS_EDIT_ANY: 'claims:edit_any',
  CLAIMS_DELETE: 'claims:delete',
  CLAIMS_VIEW: 'claims:view',
  CLAIMS_CALCULATE: 'claims:calculate',
  
  // Reports
  REPORTS_GENERATE: 'reports:generate',
  REPORTS_SHARE: 'reports:share',
  REPORTS_EXPORT: 'reports:export',
  
  // Team
  TEAM_INVITE: 'team:invite',
  TEAM_REMOVE: 'team:remove',
  TEAM_CHANGE_ROLES: 'team:change_roles',
  TEAM_VIEW: 'team:view',
  
  // Settings
  SETTINGS_BILLING: 'settings:billing',
  SETTINGS_BRANDING: 'settings:branding',
  ANALYTICS_VIEW: 'analytics:view',
} as const;

export type Permission = typeof Permissions[keyof typeof Permissions];

interface PermissionsContextType {
  role: string;
  roleDisplay: string;
  roleColor: string;
  permissions: Permission[];
  loading: boolean;
  error: string | null;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (...permissions: Permission[]) => boolean;
  hasAllPermissions: (...permissions: Permission[]) => boolean;
  canEditClaim: (claimOwnerId: string, currentUserId: string) => boolean;
  refresh: () => Promise<void>;
}

export const PermissionsContext = createContext<PermissionsContextType | null>(null);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [role, setRole] = useState<string>('viewer');
  const [roleDisplay, setRoleDisplay] = useState<string>('Viewer');
  const [roleColor, setRoleColor] = useState<string>('var(--color-text-muted)');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    // Wait for Clerk to be loaded and user to be signed in
    if (!isLoaded) {
      console.log('Clerk not loaded yet, waiting...');
      return;
    }

    if (!isSignedIn) {
      console.log('User not signed in, using viewer permissions');
      setRole('viewer');
      setPermissions([Permissions.CLAIMS_VIEW, Permissions.REPORTS_GENERATE]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get auth token for API call
      const token = await getToken();
      console.log('Got token:', token ? `${token.substring(0, 20)}...` : 'null');

      if (!token) {
        console.warn('No auth token available after sign-in, defaulting to viewer permissions');
        setRole('viewer');
        setPermissions([Permissions.CLAIMS_VIEW, Permissions.REPORTS_GENERATE]);
        return;
      }

      // Use centralized API utility with auth header
      const data = await api.get('/api/v1/team/my-permissions', {
        showErrorToast: false,
        // SeatRouteGuard renders null while permissions load; a hung request must
        // degrade (error path → loading false) instead of blanking every route.
        timeoutMs: 15000,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Permissions fetched successfully:', data);
      setRole(data.role || 'viewer');
      setRoleDisplay(data.role_display || 'Viewer');
      setRoleColor(data.role_color || 'var(--color-text-muted)');
      setPermissions(data.permissions || []);
    } catch (err: any) {
      console.error('Failed to fetch permissions:', err);
      setError(err.message);
      // Default to viewer with minimal permissions
      setRole('viewer');
      setPermissions([Permissions.CLAIMS_VIEW, Permissions.REPORTS_GENERATE]);
    } finally {
      setLoading(false);
    }
  }, [getToken, isLoaded, isSignedIn]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback((permission: Permission): boolean => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((...perms: Permission[]): boolean => {
    return perms.some(p => permissions.includes(p));
  }, [permissions]);

  const hasAllPermissions = useCallback((...perms: Permission[]): boolean => {
    return perms.every(p => permissions.includes(p));
  }, [permissions]);

  const canEditClaim = useCallback((claimOwnerId: string, currentUserId: string): boolean => {
    // Owner/Admin can edit any claim
    if (hasPermission(Permissions.CLAIMS_EDIT_ANY)) {
      return true;
    }
    // Analyst can edit own claims
    if (hasPermission(Permissions.CLAIMS_EDIT_OWN)) {
      return claimOwnerId === currentUserId;
    }
    return false;
  }, [hasPermission]);

  return (
    <PermissionsContext.Provider
      value={{
        role,
        roleDisplay,
        roleColor,
        permissions,
        loading,
        error,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        canEditClaim,
        refresh: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

/**
 * Null-safe variant: returns the permissions context, or null when no
 * PermissionsProvider is mounted (does NOT throw). Use in components that may
 * render outside the provider tree (e.g. the offline visual harness) and only
 * need to read the role opportunistically.
 */
export function usePermissionsOptional(): PermissionsContextType | null {
  return useContext(PermissionsContext);
}

// Utility component to conditionally render based on permissions
export function RequirePermission({ 
  permission, 
  children, 
  fallback = null 
}: { 
  permission: Permission | Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { hasPermission, hasAnyPermission } = usePermissions();
  
  const hasAccess = Array.isArray(permission) 
    ? hasAnyPermission(...permission)
    : hasPermission(permission);
  
  if (!hasAccess) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

// HOC for protecting entire components
export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  permission: Permission | Permission[],
  FallbackComponent?: React.ComponentType
) {
  return function WithPermissionComponent(props: P) {
    const { hasPermission, hasAnyPermission, loading } = usePermissions();
    
    if (loading) {
      return <div className="animate-pulse rounded h-8 w-32" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />;
    }
    
    const hasAccess = Array.isArray(permission)
      ? hasAnyPermission(...permission)
      : hasPermission(permission);
    
    if (!hasAccess) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return null;
    }
    
    return <WrappedComponent {...props} />;
  };
}
